import { useState } from 'react'
import { Loader2, PackagePlus } from 'lucide-react'
import { useProducts } from '@/api/hooks'
import type { components } from '@/api/schema'
import { Button } from '@/components/ui/button'
import {
  PickerMenu,
  PickerMenuContent,
  PickerMenuItem,
  PickerMenuList,
  PickerMenuSearch,
  PickerMenuTrigger,
} from '@/components/picker-menu'
import { useDebouncedValue } from '@/hooks/use-debounced-value'
import { formatMoney } from '@/lib/utils'

export type CatalogPickerProduct = components['schemas']['DashboardProductListRead']

function numericShopifyId(value?: string | null) {
  if (!value) return null
  const id = Number(value.split('/').at(-1))
  return Number.isSafeInteger(id) && id > 0 ? id : null
}

function catalogProductCanShip(product: CatalogPickerProduct) {
  return Boolean(
    numericShopifyId(product.shopify_product_id) &&
    numericShopifyId(product.shopify_variant_id) &&
    product.current_price?.store_price,
  )
}

export function CatalogProductPicker({
  onSelect,
  disabled = false,
  storeOnly = true,
  triggerLabel = 'Search catalog to add product',
  menuClassName = 'w-[min(34rem,calc(100vw-2rem))]',
}: {
  onSelect: (product: CatalogPickerProduct) => void
  disabled?: boolean
  /** Only products we can sell on the store right now (a Shopify listing and a price). */
  storeOnly?: boolean
  triggerLabel?: string
  /** Desktop menu width. */
  menuClassName?: string
}) {
  const [open, setOpen] = useState(false)
  const [search, setSearch] = useState('')
  const debouncedSearch = useDebouncedValue(search, 250)
  const products = useProducts({
    page: 1,
    limit: 8,
    search: debouncedSearch.trim() || undefined,
    has_price: storeOnly || undefined,
    on_shopify: storeOnly || undefined,
    discontinued: false,
  })

  const canPick = (product: CatalogPickerProduct) => !storeOnly || catalogProductCanShip(product)
  const choose = (product: CatalogPickerProduct) => {
    if (!canPick(product)) return
    onSelect(product)
    setOpen(false)
    setSearch('')
  }

  return (
    <PickerMenu
      open={open}
      onOpenChange={(nextOpen) => {
        if (!disabled) setOpen(nextOpen)
        if (!nextOpen) setSearch('')
      }}
    >
      <PickerMenuTrigger asChild>
        <Button
          type="button"
          variant="outline"
          className="w-full justify-start"
          disabled={disabled}
        >
          <PackagePlus /> {triggerLabel}
        </Button>
      </PickerMenuTrigger>
      <PickerMenuContent title="Add product" titleOnPhoneOnly align="start" className={menuClassName}>
        <PickerMenuSearch
          value={search}
          onChange={setSearch}
          placeholder="Search model, product, or manufacturer"
          ariaLabel="Search product catalog"
          className="sm:border-t-0"
        />
        <PickerMenuList className="max-h-96">
          {products.isLoading ? (
            <div className="flex min-h-24 items-center justify-center gap-2 text-sm text-muted-foreground">
              <Loader2 className="size-4 animate-spin" /> Loading products
            </div>
          ) : products.isError ? (
            <p className="px-3 py-6 text-center text-sm text-destructive">
              Product search failed. Try again.
            </p>
          ) : products.data?.items.length ? (
            products.data.items.map((product) => {
              const canShip = canPick(product)
              return (
                <PickerMenuItem
                  key={product.uuid}
                  disabled={!canShip}
                  onSelect={() => choose(product)}
                  className="min-h-16 items-start gap-3 px-3 py-2.5"
                >
                  <span className="flex size-10 shrink-0 items-center justify-center rounded-md bg-muted">
                    <PackagePlus className="size-4" />
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block truncate font-medium">
                      {product.store_title || product.model}
                    </span>
                    <span className="block truncate text-xs text-muted-foreground">
                      {product.manufacturer_name} · {product.model}
                    </span>
                    {!canShip ? (
                      <span className="block text-xs text-destructive">
                        Missing Shopify ID or price
                      </span>
                    ) : null}
                  </span>
                  <span className="shrink-0 text-sm font-medium tabular-nums">
                    {formatMoney(product.current_price?.store_price)}
                  </span>
                </PickerMenuItem>
              )
            })
          ) : (
            <p className="px-3 py-6 text-center text-sm text-muted-foreground">
              No matching products
            </p>
          )}
        </PickerMenuList>
      </PickerMenuContent>
    </PickerMenu>
  )
}
